import type { EnergyCost, OwnershipCosts, RoadTax, RoadTaxExemption, RoadTaxKeeper, YoungtimerStatus } from '../api/types'
import { toDutch } from '../domain/disclaimers'
import { formatEuro, formatLongDate, formatNumber, formatPrice } from '../domain/format'
import {
  capitalise,
  energyCostNoun,
  formatEnergyConsumption,
  isElectricEnergy,
  priceLevel,
  isVanRoadTax,
  roadTaxTitle,
  roadTaxYear,
  yearsUntil,
} from '../domain/ownership'
import { rows } from '../domain/rows'
import { Badge, DataRows, DataSubheading, PremiumBlock } from './Section'

/**
 * What the vehicle costs to keep.
 *
 * The card holds three kinds of claim and they must not be dressed alike. The tax milestones are
 * **dates**: they follow from one published rule and this vehicle's own date of first admission, so
 * they are either right or absent. The energy figure is an **estimate** built on a national-average
 * mileage and a price that moves week to week — which is why its assumptions are on screen beside
 * the answer rather than behind it. The road tax is **calculated** from the law's own tables for a
 * named year, per province, because the RDW does not publish where the keeper lives. The caveats
 * travel with each part.
 *
 * Order follows how many readers ask: what a year of driving costs, then the road tax, then the
 * age-related dates, which matter to fewer of them.
 */
export function OwnershipSection({ ownership, roadTax }: { ownership?: OwnershipCosts; roadTax?: RoadTax }) {
  const { roadTaxExemption, youngtimer, energy } = ownership ?? {}

  const taxRows = rows(
    ...(roadTaxExemption ? exemptionRows(roadTaxExemption) : []),
    ...(youngtimer ? [youngtimerRow(youngtimer)] : []),
  )

  const energyRows = energy ? rows(...energyCostRows(energy)) : []

  // Each half keeps its own caveats directly under it. Pooled at the foot of the card, the tax
  // caveats ("de aanslag van de Belastingdienst is bepalend") land under the fuel estimate and
  // read as if they were about the fuel.
  const taxDisclaimers = toDutch(roadTaxExemption?.disclaimers)
  const energyDisclaimers = toDutch(energy?.disclaimers)

  // The headings tell the two groups apart, so they only earn their place when there are two. On a
  // vehicle with no estimate (no consumption figure, or no current price) a lone
  // "Motorrijtuigenbelasting en bijtelling" under a subtitle that already says so is just noise.
  const grouped = taxRows.length > 0 && energyRows.length > 0

  // Running costs first: what a year of driving costs is the question every reader has, while the
  // exemption and youngtimer dates matter to fewer of them.
  return (
    <div className="ownership">
      {energy && energyRows.length > 0 && (
        <>
          {grouped && <DataSubheading>{`${capitalise(energyCostNoun(energy))} per jaar`}</DataSubheading>}
          <p className="ownership__estimate">
            Een schatting, geen meting. De aannames staan erbij, zodat je ze kunt vervangen door je
            eigen kilometrage en prijs.
          </p>
          <DataRows items={energyRows} />
          <Disclaimers items={energyDisclaimers} />
        </>
      )}

      {roadTax && <RoadTaxBlock roadTax={roadTax} />}

      {taxRows.length > 0 && (
        <>
          {(grouped || roadTax) && <DataSubheading>Vrijstelling en bijtelling</DataSubheading>}
          <DataRows items={taxRows} />
          <Disclaimers items={taxDisclaimers} />
        </>
      )}
    </div>
  )
}

/**
 * The road tax for this vehicle: one row per province for a car, one per kind of keeper for a van.
 *
 * Every province is listed because the RDW does not publish where the keeper lives, and the
 * provincial opcenten are a large share of the bill: picking one would be a guess presented as
 * this car's tax. No province is highlighted as cheapest or dearest either; the list is already
 * ordered by amount, and a buyer only cares about their own.
 *
 * A van pays no opcenten, so province does not matter, but an entrepreneur using it in the business
 * pays a different rate from anyone else, and who the keeper will be is the buyer's to know. Both
 * rows are shown for the same reason every province is: choosing one would be a guess. The private
 * row is absent when the API cannot state it, and a caveat then says why.
 *
 * The year is in the lead sentence rather than only in the caveats. After 1 January the table may
 * still be last year's, and a reader has to be able to see that without opening the small print.
 *
 * Premium as a block, not as a card: the energy estimate above it is priced from figures anyone can
 * look up, while a per-plate amount for every province is what nobody else publishes.
 */
function RoadTaxBlock({ roadTax }: { roadTax: RoadTax }) {
  const van = isVanRoadTax(roadTax)
  const amountRows = rows(
    ...(van
      ? (roadTax.byKeeper ?? []).map((keeper) => ({
          label: KEEPER_LABELS[keeper.keeper],
          value: amount(keeper.quarterlyEuro, keeper.monthlyEuro),
        }))
      : roadTax.byProvince.map((province) => ({
          label: province.province,
          value: amount(province.quarterlyEuro, province.monthlyEuro),
        }))),
  )

  return (
    <PremiumBlock title={roadTaxTitle(roadTax)}>
      <p className="ownership__estimate">
        Volgens de tarieven van {roadTaxYear(roadTax)}, voor een {van ? 'bestelauto' : 'auto'} op{' '}
        {roadTax.fuelType.toLowerCase()} met een {roadTax.massBasis} van {formatNumber(roadTax.massKg, 'kg')}.{' '}
        {van
          ? 'Voor een bestelauto hangt het bedrag af van wie de houder is: een ondernemer die hem voor zijn bedrijf gebruikt, betaalt een ander tarief dan een particulier.'
          : 'Het bedrag hangt af van de provincie waar de auto op naam staat.'}
      </p>
      <DataRows items={amountRows} />
      <Disclaimers items={toDutch(roadTax.disclaimers)} />
    </PremiumBlock>
  )
}

const KEEPER_LABELS: Record<RoadTaxKeeper, string> = {
  Business: 'Ondernemer',
  Private: 'Particulier',
}

function amount(quarterlyEuro: number, monthlyEuro: number) {
  return (
    <>
      {formatEuro(quarterlyEuro)} per kwartaal
      <span className="value-aside">(ongeveer {formatEuro(monthlyEuro)} per maand)</span>
    </>
  )
}

function Disclaimers({ items }: { items: string[] }) {
  if (items.length === 0) {
    return null
  }

  return (
    <ul className="card__footnote ownership__disclaimers">
      {items.map((disclaimer) => (
        <li key={disclaimer}>{disclaimer}</li>
      ))}
    </ul>
  )
}

/**
 * The age exemption, as a date rather than as a yes or no.
 *
 * "Nee" would be the accurate answer for a car in its thirties and a useless one. The date turns
 * "old" into something a buyer can count towards, which is the entire reason the API sends
 * `exemptFrom` whether or not the exemption has arrived. A vehicle from 1988 or later gets none and
 * is told so, because that is the answer that changes what an old car costs to keep.
 */
function exemptionRows(exemption: RoadTaxExemption) {
  const from = exemption.exemptFrom ? formatLongDate(exemption.exemptFrom) : undefined
  const years = exemption.exemptFrom ? yearsUntil(exemption.exemptFrom) : undefined

  return [
    {
      label: 'Vrijstelling motorrijtuigenbelasting',
      value: !exemption.exemptFrom ? (
        <>Geen, ook niet op hogere leeftijd</>
      ) : exemption.isExempt ? (
        <>
          <Badge tone="positive">Vrijgesteld</Badge> sinds {from}
        </>
      ) : (
        <>
          Vanaf {from}
          {years !== undefined && <span className="value-aside">(over {formatNumber(years)} jaar)</span>}
        </>
      ),
    },
    // Only stated when it applies. A row saying "niet van toepassing" on every modern car would
    // teach people that this whole card is boilerplate — and the scheme's conditions, which are
    // severe, are in the caveats below and only belong there when they are actually in force.
    {
      label: 'Overgangsregeling (kwarttarief)',
      value: exemption.transitionalSchemeApplies ? <Badge tone="warning">Van toepassing</Badge> : undefined,
    },
  ]
}

/**
 * The youngtimer treatment of the bijtelling.
 *
 * Only relevant to someone running the car through a business — but for them it is often the whole
 * reason an old car is on the shortlist at all, so it is stated plainly rather than explained at
 * length. The end date rides in the value: a car that qualifies until 1 January is not the same
 * answer as one that keeps it, and the limit is being raised.
 */
function youngtimerRow(youngtimer: YoungtimerStatus) {
  const from = formatLongDate(youngtimer.qualifiesFrom)
  const until = youngtimer.qualifiesUntil ? formatLongDate(youngtimer.qualifiesUntil) : undefined

  return {
    label: `Youngtimer voor de bijtelling (${youngtimer.qualifyingAgeYears} jaar)`,
    value: youngtimer.qualifies ? (
      <>
        <Badge tone={until ? 'warning' : 'positive'}>Ja</Badge> sinds {from}
        {until && <span className="value-aside">(tot {until}, daarna geldt een hogere leeftijdsgrens)</span>}
      </>
    ) : (
      <>
        Vanaf {from}
        {until && <span className="value-aside">(tot {until})</span>}
      </>
    ),
  }
}

/**
 * The estimate, with the two assumptions that produced it.
 *
 * The price and the annual distance are rows rather than a footnote because they are inputs a
 * reader may well disagree with, and a figure whose inputs are hidden invites more trust than it
 * has earned. The consumption is this vehicle's own, from the type approval — the one part of the
 * calculation that is a fact about this car.
 */
function energyCostRows(energy: EnergyCost) {
  const level = priceLevel(energy)
  const electric = isElectricEnergy(energy)

  return [
    {
      label: `Geschatte ${energyCostNoun(energy)}`,
      value: (
        <>
          {formatEuro(energy.annualCostEuro)} per jaar
          {/* The source rides here because CBS's reuse terms ask for the credit beside the figure
              ("CBS/Travelcard" for pump prices); a separate row for it was noise to a reader. */}
          <span className="value-aside">
            (ongeveer {formatEuro(energy.monthlyCostEuro)} per maand
            {energy.priceSource && `, bron: ${energy.priceSource}`})
          </span>
        </>
      ),
    },
    { label: electric ? 'Energiebron' : 'Brandstof', value: energy.energySource },
    { label: 'Verbruik', value: formatEnergyConsumption(energy) },
    {
      label: `Gehanteerde prijs per ${energy.consumptionUnit}`,
      value: (
        <>
          {formatPrice(energy.pricePerUnitEuro)}
          {level && <span className="value-aside">(prijspeil {formatLongDate(level)})</span>}
        </>
      ),
    },
    { label: 'Aangenomen kilometrage', value: formatNumber(energy.annualDistanceKm, 'km per jaar') },
  ]
}
